import React from "react";
import { Container, UncontrolledTooltip } from "reactstrap";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInstagram } from "@fortawesome/free-brands-svg-icons";

import { ACCESSIBILITY_PATH, LEGAL_PATHS } from "config/legal";
import "./MainFooter.css";

const SOCIAL_URL =
  "https://www.instagram.com/codenec101?igsh=MXI3cWhvdDA2a2hseA==";

const MainFooter = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").slice(0, 2);

  return (
    <footer className="cnf">
      <span className="cn-grain" aria-hidden="true" />

      <Container className="cnf-inner">
        <div className="cnf-grid">
          {/* ------------------------------ brand ------------------------- */}
          {/* The wordmark is aria-hidden because the logo's alt already names
              the link; reading both would say "Codenec Codenec".
              Footer links stay plain <a>, not router <Link>: a full load
              starts the next page at the top, while a <Link> from down here
              would keep this scroll position (LandingPage11 does not reset
              it). */}
          <div className="cnf-col cnf-col--brand">
            <a href="/" className="cnf-brand">
              <img
                alt="Codenec"
                className="cnf-brand__mark"
                src={require("assets/img/brand/CodenecLogoNoLettersTransparent.png")}
              />
              <span aria-hidden="true">Codenec</span>
            </a>

            <p className="cnf-tagline">{t("footer.getInTouchT")}</p>

            {/* Instagram is the only channel. There used to be a "Facebook"
                button here too, but it pointed at this same Instagram URL. */}
            <div className="cnf-socials">
              <a
                className="cnf-social"
                href={SOCIAL_URL}
                id="tooltip829810202"
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram"
              >
                <FontAwesomeIcon icon={faInstagram} />
              </a>
              <UncontrolledTooltip delay={0} target="tooltip829810202">
                {t("footer.followUs")}
              </UncontrolledTooltip>
            </div>
          </div>

          {/* ----------------------------- company ------------------------ */}
          {/* The two contact pages stay separate (see App.js): Contact is the
              sales conversation every home-page CTA opens, Support is the
              older /contactus page for existing students and clients. */}
          <div className="cnf-col">
            <h2 className="cnf-head">{t("footer.company")}</h2>
            <ul className="cnf-links">
              <li>
                <a className="cnf-link" href="/">
                  {t("footer.home")}
                </a>
              </li>
              <li>
                <a className="cnf-link" href="/contact">
                  {t("footer.contactUs")}
                </a>
              </li>
              <li>
                <a className="cnf-link" href="/contactus">
                  {t("footer.support")}
                </a>
              </li>
            </ul>
          </div>

          {/* ------------------------------ legal ------------------------- */}
          {/* Paths in config/legal.js. Same tab, like the links above. */}
          <div className="cnf-col">
            <h2 className="cnf-head">{t("legal.footerHead")}</h2>
            <ul className="cnf-links">
              <li>
                <a className="cnf-link" href={LEGAL_PATHS.terms}>
                  {t("legal.links.terms")}
                </a>
              </li>
              <li>
                <a className="cnf-link" href={LEGAL_PATHS.privacy}>
                  {t("legal.links.privacy")}
                </a>
              </li>
              <li>
                <a className="cnf-link" href={LEGAL_PATHS.refunds}>
                  {t("legal.links.refunds")}
                </a>
              </li>
              <li>
                <a className="cnf-link" href={ACCESSIBILITY_PATH}>
                  {t("accessibilityPage.title")}
                </a>
              </li>
              {/* The plans page holds the plan terms, refunds and the seller. */}
              <li>
                <a className="cnf-link" href="/studio/plans">
                  {t("footer.studioPlans")}
                </a>
              </li>
            </ul>
          </div>

          {/* ---------------------------- languages ----------------------- */}
          <div className="cnf-col">
            <h2 className="cnf-head">{t("footer.languages2")}</h2>
            <ul className="cnf-links">
              <li>
                <button
                  type="button"
                  lang="en"
                  aria-pressed={lang === "en"}
                  className={`cnf-link${lang === "en" ? " is-active" : ""}`}
                  onClick={() => i18n.changeLanguage("en")}
                >
                  English
                </button>
              </li>
              <li>
                <button
                  type="button"
                  lang="es"
                  aria-pressed={lang === "es"}
                  className={`cnf-link${lang === "es" ? " is-active" : ""}`}
                  onClick={() => i18n.changeLanguage("es")}
                >
                  {"Español"}
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="cnf-bottom">
          {/* CODENEC, LLC is the legal name: never translated. */}
          <div className="cnf-copy">
            &copy; <span className="cn-num">{new Date().getFullYear()}</span>{" "}
            CODENEC, LLC. {t("footer.rights")}
          </div>
        </div>

        {/* Decorative sign-off: the name, very large and barely there. */}
        <div className="cnf-wordmark" aria-hidden="true">
          Codenec
        </div>
      </Container>
    </footer>
  );
};

export default MainFooter;
